import { Injectable } from '@angular/core';
import {
    ModuleUserAccessMap,
    ObjectEntityType,
    ObjectTypeToAccessPolicyRank,
    SingleAccessPolicyAuthMap,
    SingleEntityAccessPolicy,
    UserAccessService
} from '@predictiveindex/client-permissions-lib';
import {
    InitDataService,
    ModuleUserAccessService
} from '../../system-services';

@Injectable({
    providedIn: 'root'
})

/**
 *  Permissions Service for breaking down user -> entity access policy
 *  Dependent on useraccessmap from UserAccess.service.
 */
export class UserEntityPermissionsService {
    // User Access Level Map (module, {accessLevel, VM})
    userAccessLevelMap: Map<string, ModuleUserAccessMap> = new Map();

    // EntityObject: {entity_id: entity_access_policy}
    entityObjectPermissions: Partial<{
        [key in ObjectEntityType]: Map<string, string>;
    }> = {};

    anyEntityId = 'any';

    constructor(
        private userAccessService: UserAccessService,
        private initDataService: InitDataService,
        private moduleUserAccessService: ModuleUserAccessService
    ) {}

    /**
     * All in one permission loader for a single object type
     *
     * @param companyGuid
     * @param userGuid
     * @param type
     */
    async loadEntityPermissions(type: ObjectEntityType) {
        const companyGuid = this.initDataService.user.companyId;
        const userGuid = this.initDataService.user.userGuid;
        // just in case init data does not load
        await Promise.all([
            this.getStoreUserAccessMap(),
            this.getStoreEntityObjectPermissions(companyGuid, userGuid, type)
        ]);
    }

    /**
     * @param entityId
     * @param accessPolicy
     * @returns
     */
    doesUserHaveAccess(
        entityId: string,
        accessPolicy: SingleEntityAccessPolicy
    ): boolean {
        if (!this.userAccessLevelMap || this.userAccessLevelMap.size === 0) {
            console.debug(
                'user entity permissions service has not been initialized. Please initialize with loadEntityPermissions'
            );
            return false;
        }

        // Static access policy authorization. later can be fetched from permissions-lib.
        // [ObjectEntityType, ModuleName, PermissionsModuleAccessLevel]
        const singleAPRequirements =
            SingleAccessPolicyAuthMap.get(accessPolicy);
        if (singleAPRequirements == null) {
            console.error('unsupported access policy detected', accessPolicy);
            return false;
        }

        // check module access level
        if (
            this.moduleUserAccessService.hasModuleAccessLevelOrAbove(
                singleAPRequirements[1],
                singleAPRequirements[2]
            )
        ) {
            return true;
        }

        // get this user's access policy against current entity
        if (entityId === this.anyEntityId) {
            return this.doesMatchAnyAccessPolicy(
                singleAPRequirements[0],
                accessPolicy
            );
        }

        // check single entity map
        // if not init
        const objPermissionsMap =
            this.entityObjectPermissions[singleAPRequirements[0]];

        const userSingleEntityAccessPolicy = objPermissionsMap?.get(entityId);
        if (!userSingleEntityAccessPolicy) {
            return false;
        }
        // return true if user's access policy is equal or higher than the ask.
        // on undefined, return false
        return (
            this.isEqualOrHigherAccessPolicy(
                singleAPRequirements[0],
                userSingleEntityAccessPolicy,
                accessPolicy
            ) ?? false
        );
    }

    doesUserHaveAnyAccess(accessPolicy: SingleEntityAccessPolicy): boolean {
        return this.doesUserHaveAccess(this.anyEntityId, accessPolicy);
    }

    /**
     * retrieves all the user's entity policies for every entity instance for a given object entity type
     * stores it within EntityObjectPermissions map
     *
     * @param companyGuid
     * @param userGuid
     * @param type
     */
    async getStoreEntityObjectPermissions(
        companyGuid: string,
        userGuid: string,
        type: ObjectEntityType
    ) {
        // get single access lists
        const singleAccessList =
            await this.userAccessService.getDirectEntityAccessListBySubjectAsync(
                companyGuid,
                userGuid,
                type
            );

        // populate map
        const newMap = new Map<string, string>();
        singleAccessList.forEach((x) => {
            const exists = newMap.get(x[0]);
            // take higher access policy
            newMap.set(
                x[0],
                exists ? this.getHigherAccessPolicy(type, exists, x[1]) : x[1]
            );
        });
        this.entityObjectPermissions[type] = newMap;
    }

    /**
     * calls getUserModuleAccessMapAsync when userAccessService useraccessmap is not initialized.
     */
    async getStoreUserAccessMap() {
        if (!this.initDataService.userAccessLevelMap) {
            this.initDataService.userCompanyPermissionCollection =
                await this.initDataService.getUserPermissionsCollection();
            this.initDataService.userAccessLevelMap =
                this.initDataService.getUserAccessLevelMap(
                    this.initDataService.userCompanyPermissionCollection
                );
        }
        this.userAccessLevelMap = this.initDataService.userAccessLevelMap;
    }

    /**
     * returns if any access policy of given type can be granted in the map.
     * supports access policy hierarchy. ex) edit -> read
     */
    private doesMatchAnyAccessPolicy(
        type: ObjectEntityType,
        accessPolicy: SingleEntityAccessPolicy
    ) {
        const objPermissionsMap = this.entityObjectPermissions[type];
        const accessHierarchy = ObjectTypeToAccessPolicyRank.get(type);
        const accessPolicyIdx = accessHierarchy.findIndex(
            (x) => x === accessPolicy
        );
        if (accessPolicyIdx === -1) {
            return false;
        }
        const accessHierarchyStrArray = accessHierarchy.slice(
            0,
            accessPolicyIdx + 1
        );
        return [...objPermissionsMap.values()].some((x) =>
            accessHierarchyStrArray.some(
                (a) => (x as SingleEntityAccessPolicy) === a
            )
        );
    }

    /**
     * returns higher access policy between ap1 and ap2.
     * returns ap1 if ranking order is falsy or if neither is found
     * returns ap1 if same rank
     *
     * assumes ranking order is ordered from highest to lowest in array
     */
    private getHigherAccessPolicy(
        type: ObjectEntityType,
        ap1: string,
        ap2: string
    ) {
        const ap1HigherThan2 = this.isEqualOrHigherAccessPolicy(type, ap1, ap2);
        if (!ap1HigherThan2) {
            return ap1;
        }
        return ap1HigherThan2 ? ap1 : ap2;
    }

    /**
     * returns boolean is thisAp equal or higher than thatAp
     * returns false when data is unavailable to determine
     */
    private isEqualOrHigherAccessPolicy(
        type: ObjectEntityType,
        thisAp: string,
        thatAp: string
    ) {
        const rank = ObjectTypeToAccessPolicyRank.get(type);
        if (!rank) {
            return false;
        }

        for (const r of rank) {
            if (r == thisAp) {
                return true;
            } else if (r == thatAp) {
                return false;
            }
        }
        return false;
    }
}
